import React, { Dispatch, SetStateAction, useState, useRef, useEffect } from 'react';
import { ExternalLink, HelpCircle, X } from 'lucide-react';
import axios from 'axios';
import logoHeaderOS from '../../../src/assets/review/logoHeaderOS.png';
import logoAddSiret from '../../../src/assets/review/logoAddSiret.svg';

interface ModalOpinionSystemConnectProps {
    onClose: () => void;
    isLoading: boolean;
    setShowConnectionModal: Dispatch<SetStateAction<boolean>>;
    onSuccess?: () => void;
    userId?: any;
    isConnected?: boolean;
}

const ModalOpinionSystemConnect: React.FC<ModalOpinionSystemConnectProps> = ({
    onClose,
    isLoading: externalIsLoading,
    setShowConnectionModal,
    onSuccess,
    userId,
    isConnected = false,
}) => {
    const [siret, setSiret] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);
    const [verificationResult, setVerificationResult] = useState<any>(null);
    const [showMessageModal, setShowMessageModal] = useState(false);
    const [modalMessage, setModalMessage] = useState('');
    const [modalMessageType, setModalMessageType] = useState<'error'>('error');

    const verifyTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const handleSiretChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSiret(e.target.value);
        setVerificationResult(null);

        if (verifyTimeoutRef.current) {
            clearTimeout(verifyTimeoutRef.current);
        }

        const cleanSiret = e.target.value.replace(/\s/g, '');
        if (cleanSiret.length === 14) {
            verifyTimeoutRef.current = setTimeout(() => {
                if (e.target.value.replace(/\s/g, '') === cleanSiret) {
                    handleVerifySiret(cleanSiret);
                }
            }, 800);
        }
    };

    const handleVerifySiret = async (providedSiret?: string) => {
        const cleanSiret = providedSiret || siret.replace(/\s/g, '');

        if (cleanSiret.length !== 14 && cleanSiret !== '11168') {
            return;
        }

        setIsVerifying(true);

        try {
            const response = await axios.post('/api/opinion-system/verify-siret', {
                siret: cleanSiret,
            });

            const result = response.data;

            if (result.valid) {
                setVerificationResult(result.company);
            }
        } catch (err: any) {
            console.error('Erreur vérification:', err);
        } finally {
            setIsVerifying(false);
        }
    };

    const handleSaveSiret = async () => {
        if (!verificationResult) {
            const cleanSiret = siret.replace(/\s/g, '');

            try {
                const response = await axios.post('/api/opinion-system/verify-siret', {
                    siret: cleanSiret,
                });

                const result = response.data;

                if (!result.valid) {
                    setModalMessage(result.error || 'SIRET non trouvé dans Opinion System');
                    setModalMessageType('error');
                    setShowMessageModal(true);
                    return;
                }
            } catch (err: any) {
                if (err.response?.data?.error) {
                    setModalMessage(err.response.data.error);
                } else {
                    setModalMessage('Erreur lors de la vérification du SIRET');
                }
                setModalMessageType('error');
                setShowMessageModal(true);
                return;
            }
        }

        setIsLoading(true);

        try {
            const cleanSiret = siret.replace(/\s/g, '');

            const apiRoute = isConnected
                ? '/api/opinion-system/update-siret'
                : '/api/opinion-system/set-siret';

            if (isConnected) {
                await axios.put(apiRoute, { userId, siret: cleanSiret });
            } else {
                await axios.post(apiRoute, { userId, siret: cleanSiret });
            }

            onClose();
            setTimeout(() => {
                onSuccess?.();
            }, 500);
        } catch (err: any) {
            const errorMessage = isConnected
                ? 'Erreur lors de la modification du SIRET'
                : "Erreur lors de l'enregistrement du SIRET";

            if (err.response?.data?.error) {
                setModalMessage(err.response.data.error);
            } else {
                setModalMessage(errorMessage);
            }
            setModalMessageType('error');
            setShowMessageModal(true);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        return () => {
            if (verifyTimeoutRef.current) {
                clearTimeout(verifyTimeoutRef.current);
            }
        };
    }, []);

    const OpinionSystemLogo = () => (
        <div className="w-[158.68px] h-16 relative overflow-hidden">
            <div className="w-full h-full flex items-center justify-center rounded-lg">
                <img className="" src={logoHeaderOS} alt="logo os" />
            </div>
        </div>
    );

    return (
        <>
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="w-[720px] p-8 relative bg-[#FAFAFA] rounded-3xl flex flex-col justify-start items-start gap-5">
                    <button
                        onClick={onClose}
                        className="absolute right-6 top-6 p-2.5 bg-white rounded-full border border-[#D4D4D4] hover:bg-gray-50 transition-colors"
                    >
                        <X className="w-3 h-3 text-[#404040]" />
                    </button>

                    <OpinionSystemLogo />

                    <div className="self-stretch flex flex-col justify-start items-start gap-6">
                        <div className="self-stretch flex flex-col justify-start items-start gap-4">
                            {!isConnected && (
                                <div className="self-stretch flex justify-start items-stretch gap-4">
                                    <div className="flex-1 grow p-3 bg-gradient-to-b from-white/0 to-white rounded-2xl shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] border-2 border-white flex flex-col justify-start items-start gap-2">
                                        <div className="w-7 h-7 bg-[#62AA47] rounded-lg flex justify-center items-center">
                                            <span className="text-white text-sm font-bold">1</span>
                                        </div>
                                        <div className="self-stretch flex flex-col justify-start items-start gap-1.5">
                                            <div className="text-[#262626] text-sm font-semibold">
                                                Associez Newsfactory à Opinion System
                                            </div>
                                            <div className="text-[#737373] text-xs">
                                                Dans Opinion System, rubrique Outils {'>'} API {'>'}{' '}
                                                Ajouter un partenaire et autorisez l'accès aux
                                                données publiques et privées.
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex-1 p-3 grow bg-gradient-to-b from-white/0 to-white rounded-2xl shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] border-2 border-white flex flex-col justify-start items-start gap-2">
                                        <div className="w-7 h-7 bg-[#62AA47] rounded-lg flex justify-center items-center">
                                            <span className="text-white text-sm font-bold">2</span>
                                        </div>
                                        <div className="self-stretch flex flex-col justify-start items-start gap-1.5">
                                            <div className="text-[#262626] text-sm font-semibold">
                                                Renseignez votre SIRET
                                            </div>
                                            <div className="text-[#737373] text-xs">
                                                Dans le champ ci-dessous renseignez votre SIRET pour
                                                compléter l'activation de la fonctionnalité
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex-1 p-3 grow bg-gradient-to-b from-white/0 to-white rounded-2xl shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] border-2 border-white flex flex-col justify-start items-start gap-2">
                                        <div className="w-7 h-7 bg-[#62AA47] rounded-lg flex justify-center items-center">
                                            <span className="text-white text-sm font-bold">3</span>
                                        </div>
                                        <div className="self-stretch flex flex-col justify-start items-start gap-1.5">
                                            <div className="text-[#262626] text-sm font-semibold">
                                                Profitez d'Opinion System !
                                            </div>
                                            <div className="text-[#737373] text-xs">
                                                Vous pouvez profitez dès à présent des avis laisser
                                                sur Opinion System directement dans Newsfactory
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="self-stretch flex justify-start items-start gap-4">
                                <div className="flex-1 min-h-[100px] p-3 bg-gradient-to-r from-white/50 to-white/0 rounded-2xl shadow-[0px_3px_7px_0px_rgba(0,0,0,0.08)] border-2 border-white flex flex-col justify-between items-start overflow-hidden relative">
                                    <div className="absolute w-[132px] h-[123px] right-[-50px] top-8 opacity-70 bg-gradient-to-l from-[#62aa47] to-[#BED000] rounded-full blur-xl" />
                                    <div className="flex justify-start items-center gap-2 relative z-10">
                                        <div className="p-1.5 bg-[#E0F0D7] rounded-lg">
                                            <HelpCircle className="w-4 h-4 text-[#62AA47]" />
                                        </div>
                                        <span className="text-[#262626] text-base font-semibold">
                                            Besoin d'aide ?
                                        </span>
                                    </div>
                                    <button
                                        onClick={() =>
                                            window.open(
                                                'https://www.calameo.com/read/006173559fc8f115c9c21',
                                                '_blank',
                                            )
                                        }
                                        className="px-4 py-1.5 bg-[#62AA47] rounded-full flex justify-center items-center gap-2 hover:bg-[#5a9940] transition-colors relative z-10"
                                    >
                                        <HelpCircle className="w-2.5 h-2.5 text-white" />
                                        <span className="text-white text-xs font-semibold">
                                            Voir le guide d'utilisation
                                        </span>
                                    </button>
                                </div>

                                <div className="flex-1 min-h-[100px] p-3 bg-gradient-to-bl from-[#f2f6cc] via-[#f8fae5]/50 to-white/0 rounded-2xl shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] border-2 border-white flex flex-col justify-between items-start overflow-hidden">
                                    <div className="w-[79.34px] h-8">
                                        <img className="" src={logoHeaderOS} alt="logo os" />
                                    </div>
                                    <button
                                        onClick={() =>
                                            window.open(
                                                'https://www.opinionsystem.fr/fr-fr/',
                                                '_blank',
                                            )
                                        }
                                        className="px-4 py-1.5 bg-white rounded-full border border-[#404040] flex justify-center items-center gap-2 hover:bg-gray-50 transition-colors"
                                    >
                                        <ExternalLink className="w-2.5 h-2.5 text-[#404040]" />
                                        <span className="text-[#404040] text-xs font-semibold">
                                            Accéder à Opinion System
                                        </span>
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="self-stretch h-px bg-[#D4D4D4]" />

                        <div className="self-stretch p-3 bg-white rounded-2xl shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] border border-[#BED000]/50 flex flex-col justify-start items-start gap-3">
                            <div className="self-stretch flex justify-start items-center gap-2">
                                <div className="p-1.5 bg-[#62AA47] rounded-lg">
                                    <img className="" src={logoAddSiret} alt="logo add siret" />
                                </div>
                                <span className="text-[#262626] text-base font-semibold">{`${
                                    isConnected ? 'Modifier votre' : 'Ajouter un'
                                } SIRET`}</span>
                            </div>

                            <div className="self-stretch flex flex-col gap-4">
                                <div className="self-stretch flex flex-col gap-2">
                                    <div className="relative">
                                        <input
                                            type="text"
                                            value={siret}
                                            onChange={handleSiretChange}
                                            placeholder="Indiquez votre SIRET (ex: 123 456 789 12345)"
                                            className={`self-stretch w-full p-2 bg-white rounded border text-[13px] focus:outline-none transition-colors ${
                                                isVerifying
                                                    ? 'border-yellow-400'
                                                    : verificationResult
                                                      ? 'border-green-400'
                                                      : 'border-[#e4e4e4] focus:border-[#62AA47]'
                                            }`}
                                            maxLength={17}
                                        />
                                        {isVerifying && (
                                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                                <div className="w-4 h-4 border-2 border-[#62AA47] border-t-transparent rounded-full animate-spin"></div>
                                            </div>
                                        )}
                                    </div>

                                    {isVerifying && (
                                        <div className="text-yellow-600 text-xs flex items-center gap-1">
                                            🔍 Vérification du SIRET en cours...
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="self-stretch flex justify-between items-center">
                        <button
                            onClick={onClose}
                            className="px-5 py-1.5 bg-[#525254] rounded-full hover:bg-[#404040] transition-colors"
                        >
                            <span className="text-white text-[13px] font-semibold">Annuler</span>
                        </button>

                        <button
                            onClick={handleSaveSiret}
                            disabled={isLoading}
                            className="w-auto h-8 px-5 py-1.5 bg-[#BED000] rounded-full hover:bg-[#a8bb00] disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                        >
                            <span className="text-[#393939] text-[13px] font-semibold">
                                {isLoading ? 'En cours...' : isConnected ? 'Modifier' : 'Valider'}
                            </span>
                        </button>
                    </div>
                </div>
            </div>

            {showMessageModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
                    <div className="bg-white p-6 rounded-2xl shadow-xl max-w-md mx-4">
                        <div className="text-center">
                            {modalMessageType === 'error' && (
                                <div className="w-12 h-12 mx-auto mb-3 bg-red-100 rounded-full flex items-center justify-center">
                                    <svg
                                        className="w-6 h-6 text-red-600"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M6 18L18 6M6 6l12 12"
                                        />
                                    </svg>
                                </div>
                            )}
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                {modalMessageType === 'error' && 'Erreur de validation'}
                            </h3>
                            <p className="text-gray-600 text-sm mb-4 whitespace-pre-line text-center">
                                {modalMessage}
                            </p>
                            <button
                                onClick={() => {
                                    setShowMessageModal(false);
                                }}
                                className="w-[94px] h-8 px-5 py-1.5 bg-[#BED000] rounded-full hover:bg-[#a8bb00] transition-colors"
                            >
                                <span className="text-[#393939] text-[13px] font-semibold">
                                    Fermer
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ModalOpinionSystemConnect;
